/* ==========================================================================
   DISSOLV — motion.js
   Cinematic but restrained: scroll reveals, gentle parallax, marquees,
   count-ups, the before/after slider and the hero entrance.
   Fully disabled for prefers-reduced-motion and when the merchant turns
   motion off in theme settings.
   ========================================================================== */
(() => {
  'use strict';

  const qs = (s, c = document) => c.querySelector(s);
  const qsa = (s, c = document) => [...c.querySelectorAll(s)];
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const settings = (window.Dissolv && window.Dissolv.motion) || { enabled: true, intensity: 'standard' };
  const motionOn = settings.enabled && !reduced;

  /* ---------- Scroll reveals ---------- */
  function initReveals() {
    const els = qsa('[data-reveal]');
    if (!els.length) return;
    if (!motionOn || !('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('is-revealed'));
      return;
    }
    // Auto-stagger children of reveal groups
    qsa('[data-reveal-group]').forEach(group => {
      qsa('[data-reveal]', group).forEach((el, i) => {
        if (!el.dataset.revealDelay) el.dataset.revealDelay = String(i * 90);
      });
    });
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        el.style.setProperty('--reveal-delay', `${parseInt(el.dataset.revealDelay || '0', 10)}ms`);
        el.classList.add('is-revealed');
        // Also assert the revealed state inline: if a client holds a stale or
        // truncated cached stylesheet missing the .is-revealed rule, content
        // must still become visible (transition comes from CSS when healthy).
        el.style.opacity = '1';
        el.style.transform = 'none';
        el.style.filter = 'none';
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    els.forEach(el => io.observe(el));
  }

  /* ---------- Parallax ---------- */
  function initParallax() {
    if (!motionOn) return;
    const els = qsa('[data-parallax]');
    if (!els.length) return;
    const tracked = new Map();
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => tracked.set(e.target, e.isIntersecting));
    });
    els.forEach(el => { tracked.set(el, false); io.observe(el); });

    let ticking = false;
    function frame() {
      ticking = false;
      const vh = window.innerHeight;
      tracked.forEach((visible, el) => {
        if (!visible) return;
        const speed = parseFloat(el.dataset.parallax) || 0.12;
        const rect = el.getBoundingClientRect();
        const offset = (rect.top + rect.height / 2 - vh / 2) * -speed;
        el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
      });
    }
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(frame); }
    }, { passive: true });
    frame();
  }

  /* ---------- Marquees (seamless loop via duplication) ---------- */
  function initMarquees() {
    qsa('.marquee').forEach(marquee => {
      const track = qs('.marquee__track', marquee);
      if (!track || track.dataset.cloned) return;
      const clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.dataset.cloned = 'true';
      marquee.appendChild(clone);
    });
  }

  /* ---------- Count-up numbers ---------- */
  function initCountUps() {
    const els = qsa('[data-count-to]');
    if (!els.length) return;
    if (!motionOn || !('IntersectionObserver' in window)) {
      els.forEach(el => el.textContent = el.dataset.countTo + (el.dataset.countSuffix || ''));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        io.unobserve(el);
        const target = parseFloat(el.dataset.countTo);
        const suffix = el.dataset.countSuffix || '';
        const decimals = (String(el.dataset.countTo).split('.')[1] || '').length;
        const dur = 1600;
        const start = performance.now();
        (function tick(now) {
          const p = Math.min(1, (now - start) / dur);
          const eased = 1 - Math.pow(1 - p, 4);
          el.textContent = (target * eased).toFixed(decimals) + suffix;
          if (p < 1) requestAnimationFrame(tick);
        })(start);
      });
    }, { threshold: 0.4 });
    els.forEach(el => io.observe(el));
  }

  /* ---------- Before / after slider ---------- */
  function initBeforeAfter() {
    qsa('[data-ba]').forEach(ba => {
      const set = (clientX) => {
        const rect = ba.getBoundingClientRect();
        const pct = Math.min(96, Math.max(4, ((clientX - rect.left) / rect.width) * 100));
        ba.style.setProperty('--ba', `${pct}%`);
      };
      let dragging = false;
      ba.addEventListener('pointerdown', (e) => { dragging = true; ba.setPointerCapture(e.pointerId); set(e.clientX); });
      ba.addEventListener('pointermove', (e) => dragging && set(e.clientX));
      ba.addEventListener('pointerup', () => dragging = false);
      ba.addEventListener('pointercancel', () => dragging = false);
      const handle = qs('.ba__handle', ba);
      if (handle) {
        handle.setAttribute('tabindex', '0');
        handle.setAttribute('role', 'slider');
        handle.setAttribute('aria-label', 'Comparison slider');
        handle.addEventListener('keydown', (e) => {
          const current = parseFloat(getComputedStyle(ba).getPropertyValue('--ba')) || 50;
          if (e.key === 'ArrowLeft') { ba.style.setProperty('--ba', `${Math.max(4, current - 4)}%`); e.preventDefault(); }
          if (e.key === 'ArrowRight') { ba.style.setProperty('--ba', `${Math.min(96, current + 4)}%`); e.preventDefault(); }
        });
      }
      // Gentle intro nudge when first seen
      if (motionOn && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver(([entry]) => {
          if (!entry.isIntersecting) return;
          io.disconnect();
          let t = 0;
          const anim = setInterval(() => {
            t += 1;
            const v = 50 + Math.sin(t / 8) * 6 * Math.max(0, 1 - t / 60);
            ba.style.setProperty('--ba', `${v}%`);
            if (t > 60) clearInterval(anim);
          }, 16);
          ba.addEventListener('pointerdown', () => clearInterval(anim), { once: true });
        }, { threshold: 0.5 });
        io.observe(ba);
      }
    });
  }

  /* ---------- Hero entrance ---------- */
  function initHero() {
    const hero = qs('[data-hero]');
    if (!hero) return;
    // Split headline into word-mask spans
    const title = qs('.hero__title', hero);
    if (title && motionOn && !title.dataset.split) {
      title.dataset.split = 'true';
      const split = (node) => {
        [...node.childNodes].forEach(child => {
          if (child.nodeType === Node.TEXT_NODE) {
            const frag = document.createDocumentFragment();
            child.textContent.split(/(\s+)/).forEach(part => {
              if (!part) return;
              if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(' ')); return; }
              const w = document.createElement('span');
              w.className = 'word';
              const inner = document.createElement('span');
              inner.textContent = part;
              w.appendChild(inner);
              frag.appendChild(w);
            });
            node.replaceChild(frag, child);
          } else if (child.nodeType === Node.ELEMENT_NODE && child.tagName !== 'BR') {
            split(child);
          }
        });
      };
      split(title);
      qsa('.word > span', title).forEach((s, i) => s.parentElement.style.setProperty('--w', i) || s.style.setProperty('--w', i));
      qsa('.word', title).forEach((w, i) => w.querySelector('span').style.setProperty('--w', i));
    }
    const video = qs('video', hero);
    // When the mobile static image is active, skip only the video hydration —
    // the is-loaded reveal below must still run or the headline stays masked.
    const skipVideo = video && window.matchMedia('(max-width: 749px)').matches && video.closest('.hero--static-mobile');
    if (video && !skipVideo) {
      const desktopSrc = video.dataset.src;
      if (desktopSrc && !video.getAttribute('src')) {
        const useMobile = video.dataset.srcMobile && window.matchMedia('(max-width: 749px)').matches;
        if (useMobile && video.dataset.posterMobile) video.poster = video.dataset.posterMobile;
        video.src = useMobile ? video.dataset.srcMobile : desktopSrc;
      }
      video.muted = true;
      const play = () => video.play().catch(() => {});
      if (video.readyState >= 2) play(); else video.addEventListener('canplay', play, { once: true });
    }
    const ready = () => hero.classList.add('is-loaded');
    if (!motionOn) { ready(); return; }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => setTimeout(ready, 80));
      setTimeout(ready, 900); // safety
    } else {
      setTimeout(ready, 200);
    }
  }

  /* ---------- Boot ---------- */
  function boot() {
    initMarquees();
    initReveals();
    initParallax();
    initCountUps();
    initBeforeAfter();
    initHero();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
